<template>
  <component
    :is="isMenu || link.isRoute ? 'li' : 'router-link'"
    :to="link.path ? link.path : ''"
    class="sidebar-item"
    :class="{ 'is-active': isActive, 'mini-mode': sidebarContext.collapsed && !$sidebar.showSidebar }"
    tag="li"
    @mouseenter="isItemHovered = true"
    @mouseleave="isItemHovered = false"
  >
    <a
      v-if="isMenu"
      href="#"
      class="nav-link"
      :class="{ 'is-selected': isActive }"
      :aria-expanded="!collapsed"
      @click.prevent="collapseMenu"
    >
      <span class="nav-icon">
        <component
          v-if="typeof link.icon === 'object' || typeof link.icon === 'function'"
          :is="link.icon"
          :active="isActive || isItemHovered"
          :class="['sidebar-icon', { 'is-active': isActive || isItemHovered }]"
          :size="20"
        />
        <span v-else class="icon-text">{{ link.icon }}</span>
      </span>
      <p v-if="!sidebarContext.collapsed || $sidebar.showSidebar" class="nav-text">
        {{ link.name }}
        <chevron-icon
          class="chevron-transition"
          :style="{ transform: !collapsed ? 'rotate(180deg)' : 'rotate(0deg)' }"
        />
      </p>
    </a>

    <div v-if="$slots.default" v-show="!collapsed && (!sidebarContext.collapsed || $sidebar.showSidebar)" class="submenu">
      <ul class="submenu-list">
        <slot></slot>
      </ul>
    </div>

    <slot name="title" v-if="!isMenu && link.path">
      <component
        :is="link.isRoute === false ? 'a' : 'router-link'"
        :to="link.path"
        :href="link.path"
        class="nav-link"
        :class="{ 'is-selected': isDirectActive || isItemHovered }"
        :target="link.target"
        @click.native="linkClick"
        @mouseenter.native="isItemHovered = true"
        @mouseleave.native="isItemHovered = false"
      >
        <span class="nav-icon">
          <component
            v-if="typeof link.icon === 'object' || typeof link.icon === 'function'"
            :is="link.icon"
            :active="isDirectActive || isItemHovered"
            :class="['sidebar-icon', { 'is-active': isDirectActive || isItemHovered }]"
            :size="20"
          />
          <span v-else class="icon-text">{{ link.icon }}</span>
        </span>
        <p v-if="!sidebarContext.collapsed || $sidebar.showSidebar" :class="{ 'nav-text-hovered': !sidebarContext.collapsed && link.name.length > 25 }" class="nav-text">
          {{ link.name }}
          <span v-if="featureBadge" class="badge">{{ featureBadge }}</span>
        </p>
      </component>
    </slot>
  </component>
</template>

<script>
import ChevronIcon from '../Icons/SidebarIcons/chevron-icon.vue';

export default {
  name: 'SidebarItem',
  components: { ChevronIcon },

  props: {
    menu: { type: Boolean, default: false },
    link: {
      type: Object,
      default: () => ({ name: '', path: '', children: [] })
    }
  },

  provide() {
    return {
      addLink: this.addChild,
      removeLink: this.removeChild
    };
  },

  inject: {
    addLink: { default: null },
    removeLink: { default: null },
    autoClose: { default: true },
    sidebarContext: { default: () => ({ collapsed: false }) }
  },

  data() {
    return {
      children: [],
      collapsed: true,
      isItemHovered: false
    };
  },

  computed: {
    featureBadge() {
      return this.link.meta_data && this.link.meta_data.badge;
    },

    isMenu() {
      return (
        !!this.$slots.default ||
        (this.link.children && this.link.children.length > 0) ||
        this.menu === true
      );
    },

    isDirectActive() {
      if (this.$route && this.$route.path && this.link.path) {
        return this.$route.path === this.link.path;
      }
      return this.link.active || false;
    },

    isActive() {
      if (this.isDirectActive) return true;
      return this.children.some(function(child) {
        return child.isActive || child.isDirectActive;
      });
    },
    isVisuallySelected() {
      if (this.sidebarContext.collapsed && this.isMenu) {
        return this.isActive;
      }
      return this.isDirectActive;
    }
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler() {
        if (this.isActive && this.isMenu) {
          this.collapsed = false;
        }
      }
    }
  },

  methods: {
    addChild(item) {
      const index = this.$slots.default
        ? this.$slots.default.indexOf(item.$vnode)
        : -1;
      index > -1 ? this.children.splice(index, 0, item) : this.children.push(item);
    },

    removeChild(item) {
      const index = this.children.indexOf(item);
      if (index > -1) this.children.splice(index, 1);
    },

    linkClick() {
      if (this.autoClose && this.$sidebar && this.$sidebar.showSidebar === true && window.innerWidth <= 991) {
        this.$sidebar.displaySidebar(false);
      }
    },

    collapseMenu() {
      this.collapsed = !this.collapsed;
    }
  },

  mounted() {
    if (this.addLink) this.addLink(this);
    if (this.link.collapsed !== undefined) this.collapsed = this.link.collapsed;
    if (this.isActive && this.isMenu) this.collapsed = false;
  },

  destroyed() {
    if (this.$el && this.$el.parentNode) this.$el.parentNode.removeChild(this.$el);
    if (this.removeLink) this.removeLink(this);
  }
};
</script>

<style scoped>
.sidebar-item {
  list-style: none;
  display: block;
  width: 100%;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 10px 14px 10px 14px;
  margin: 0 0px 4px 0px;
  border-radius: 8px;
  border: 1px solid transparent;
  color: #FFFFFFD9 !important;
  font-weight: 400 !important;
  text-decoration: none;
  cursor: pointer;
  box-sizing: border-box;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.mini-mode .nav-link {
  border-radius: 12px;
}

.nav-link.is-selected {
  background-color: var(--nav-active-bg);
  border-color: var(--nav-active-border);
  color: var(--nav-selected-text) !important;
  font-weight: 500;
}

.mini-mode .nav-link.is-selected {
  background-color: var(--nav-active-bg);
  border: 1px solid var(--nav-active-border);
  margin: 0 auto 4px auto;
  justify-content: center;
}

.nav-link:hover {
  background-color: var(--nav-active-bg);
  border: 1px solid var(--nav-active-border);
  color: var(--nav-hover-text) !important;
  font-weight: 500;
}

.mini-mode .nav-link:hover {
  margin: 0 auto 4px auto;
}

.mini-mode .nav-icon {
  margin: 0;
}

.chevron-transition {
  transition: transform 0.2s ease;
}

.nav-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  height: 24px;
}

.icon-text {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
}

.nav-link.is-selected .icon-text,
.nav-link.is-selected {
  color: #0c1a24;
}

.nav-text {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 0 10px;
  font-size: 16px;
  font-weight: 500;
  min-width: 0;
  white-space: nowrap;
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
}

.nav-text-hovered {
  white-space: wrap;
}

.mini-mode .nav-text {
  display: none;
}

.submenu {
  overflow: hidden;
}

.submenu-list {
  list-style: none;
  padding: 4px 0 4px 12px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.submenu-list .nav-link {
  padding: 7px 8px 7px 8px;
  font-size: 14px;
  border-radius: 8px;
  margin: 0 0px 4px 8px;
}

.mini-mode .chevron-transition {
  display: none;
}

.badge {
  background-color: #ef4444;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  line-height: 1;
}

.mini-mode .badge {
  display: none;
}

.sidebar-icon {
  color: #FFFFFFD9;
}
.sidebar-icon.is-active {
  color: #FFFFFF;
}
</style>